import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from 'react-joyride';
import { useAuth } from './AuthContext';
import { usersApi } from '@/lib/api';

const TOUR_COMPLETED_KEY = 'tour_completed';

interface TourContextType {
  isRunning: boolean;
  startTour: () => void;
  endTour: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

// Tour steps for regular users
const userSteps: Step[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to Apartment 5H Calendar!',
    content: "Let's take a quick tour to help you get started with booking your stays.",
    disableBeacon: true,
  },
  {
    target: '[data-tour="calendar"]',
    placement: 'top',
    title: 'Your Booking Calendar',
    content: 'This calendar shows all bookings. Colored blocks indicate confirmed stays - each user has their own color.',
  },
  {
    target: '[data-tour="calendar"]',
    placement: 'top',
    title: 'Making a Booking',
    content: 'Click and drag on available dates to select your stay, then click "Request These Dates" to submit your request.',
  },
  {
    target: '[data-tour="my-bookings"]',
    placement: 'bottom',
    title: 'My Bookings',
    content: 'Track all your booking requests here - pending approval, confirmed stays, and past visits.',
  },
  {
    target: '[data-tour="property-info"]',
    placement: 'bottom',
    title: 'Property & Guides',
    content: 'Find apartment details, helpful guides, and downloadable documents in these sections.',
  },
  {
    target: 'body',
    placement: 'center',
    title: "You're All Set!",
    content: 'Click the help icon (?) in the header anytime to replay this tour. Enjoy your stay!',
    disableBeacon: true,
  },
];

// Additional steps for owners/admins
const adminSteps: Step[] = [
  {
    target: '[data-tour="admin-menu"]',
    placement: 'bottom',
    title: 'Admin Menu',
    content: 'Access administrative features from this menu to manage users, bookings, and settings.',
  },
  {
    target: '[data-tour="admin-menu"]',
    placement: 'bottom',
    title: 'Pending Requests',
    content: 'The badge shows pending booking requests. Click "Pending Requests" to review and approve or decline them.',
  },
];

// Owner-specific step
const ownerSteps: Step[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Owner Benefits',
    content: 'As an owner, your bookings are automatically approved - no waiting required!',
    disableBeacon: true,
  },
];

interface TourProviderProps {
  children: ReactNode;
}

// Check if tour was completed (localStorage backup for when API call fails/races)
function isTourCompletedLocally(): boolean {
  try {
    return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
  } catch {
    return false;
  }
}

function markTourCompletedLocally(): void {
  try {
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
  } catch {
    // Ignore localStorage errors
  }
}

export function TourProvider({ children }: TourProviderProps) {
  const { user, setUser } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Sync localStorage with server state when user logs in with tour_completed=true
  useEffect(() => {
    if (user?.tour_completed && !isTourCompletedLocally()) {
      markTourCompletedLocally();
    }
  }, [user?.tour_completed]);

  // Build steps based on user role
  const getSteps = useCallback((): Step[] => {
    if (!user) return userSteps;

    if (user.role === 'owner') {
      // Insert owner step after welcome, then add admin steps before final step
      const steps = [...userSteps];
      steps.splice(1, 0, ...ownerSteps);
      steps.splice(-1, 0, ...adminSteps);
      return steps;
    }

    if (user.role === 'admin') {
      // Add admin steps before final step
      const steps = [...userSteps];
      steps.splice(-1, 0, ...adminSteps);
      return steps;
    }

    return userSteps;
  }, [user]);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsRunning(true);
  }, []);

  const endTour = useCallback(async () => {
    setIsRunning(false);
    setStepIndex(0);

    // Mark tour as completed if user hasn't completed it yet
    if (user && !user.tour_completed) {
      // Immediately mark locally to prevent re-showing on quick navigation/refresh
      markTourCompletedLocally();
      setUser({ ...user, tour_completed: true });

      // Then persist to server (fire-and-forget, local state is already updated)
      try {
        await usersApi.markTourComplete(user.id);
      } catch (err) {
        console.error('Failed to mark tour complete on server:', err);
        // Local state and localStorage are already updated, so tour won't restart
      }
    }
  }, [user, setUser]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        endTour();
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        // Update step index for next/prev
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }
    },
    [endTour]
  );

  const steps = getSteps();

  return (
    <TourContext.Provider value={{ isRunning, startTour, endTour }}>
      {children}
      <Joyride
        steps={steps}
        stepIndex={stepIndex}
        run={isRunning}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: '#3B82F6',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: 8,
            padding: 20,
          },
          tooltipTitle: {
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 8,
          },
          tooltipContent: {
            fontSize: 14,
            lineHeight: 1.5,
          },
          buttonNext: {
            backgroundColor: '#3B82F6',
            borderRadius: 6,
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 500,
          },
          buttonBack: {
            color: '#6B7280',
            marginRight: 8,
            fontSize: 14,
          },
          buttonSkip: {
            color: '#9CA3AF',
            fontSize: 14,
          },
          spotlight: {
            borderRadius: 8,
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Get Started',
          next: 'Next',
          skip: 'Skip Tour',
        }}
      />
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
